import { useMemo, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { Icon } from "@iconify/react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";
import { BaseTable } from "../../BaseTable/BaseTable";
import MuiConfirmationDialog from "../../@material-extend/MuiConfirmationDialog";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList, useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { FEATURE } from "src/util/enums";
import { fDateTime } from "src/utils/formatTime";
import sanitizeValues from "src/utils/sanitizeValues";
import {
  getTeamUrl,
  fleetVehiclesUrl,
  fleetVehicleIdUrl,
  fleetAssignmentsUrl,
  fleetAssignmentEndUrl,
} from "src/react-query/endPoints";
import {
  FLEET_ENTITY_STATUS_OPTIONS,
  fleetVehicleValidationSchema,
  getVehicleDefaultValues,
  toList,
  fullName,
  statusChipColor,
  emptyToNull,
} from "../fleet.constants";

const getVehicleId = (row) => row?.vehicle_id ?? row?.vehicleId ?? row?.id;
const getDriverId = (row) => row?.driver_id ?? row?.driverId ?? row?.id;
const getAssignmentId = (row) =>
  row?.assignment_id ?? row?.assignmentId ?? row?.id;

// datetime-local value ("yyyy-MM-ddTHH:mm") -> MySQL-friendly string
const toDateTimeString = (value) =>
  value ? `${value.replace("T", " ")}:00` : null;

const VehiclesTab = ({ operatorId }) => {
  const { teamId } = useSelectedTeam();
  const hasCreateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "C");
  const hasUpdateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "U");
  const hasDeleteAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "D");

  const {
    data: vehiclesData,
    isFetching,
  } = useList("fleetVehicles", undefined, operatorId);
  const { data: driversData } = useList("fleetDrivers", undefined, operatorId);
  const {
    data: assignmentsData,
    isFetching: isAssignmentsFetching,
  } = useList("fleetAssignments", undefined, operatorId);

  const vehicles = useMemo(() => toList(vehiclesData), [vehiclesData]);
  const drivers = useMemo(() => toList(driversData), [driversData]);
  const assignments = useMemo(() => toList(assignmentsData), [assignmentsData]);

  const { mutate: mutateVehicle, isLoading: isVehicleMutating } = useMutate(
    "fleetVehicles",
    { id: operatorId }
  );
  const { mutate: mutateAssignment, isLoading: isAssignmentMutating } =
    useMutate("fleetAssignments", { id: operatorId });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deletingVehicle, setDeletingVehicle] = useState(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    driver_id: "",
    vehicle_id: "",
    start_at: "",
  });
  const [endingAssignment, setEndingAssignment] = useState(null);

  const openCreate = () => {
    setEditingVehicle(null);
    formik.resetForm({ values: getVehicleDefaultValues() });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingVehicle(row);
    const defaults = getVehicleDefaultValues();
    formik.resetForm({
      values: Object.keys(defaults).reduce((acc, key) => {
        acc[key] = row?.[key] ?? defaults[key] ?? "";
        return acc;
      }, {}),
    });
    setDialogOpen(true);
  };

  const onSubmit = (values, { setSubmitting }) => {
    const body = emptyToNull(sanitizeValues(values));
    const isEdit = Boolean(editingVehicle);
    mutateVehicle(
      {
        url: isEdit
          ? getTeamUrl(
              teamId,
              fleetVehicleIdUrl(operatorId, getVehicleId(editingVehicle))
            )
          : getTeamUrl(teamId, fleetVehiclesUrl(operatorId)),
        method: isEdit ? "PUT" : "POST",
        body,
      },
      {
        onSuccess: () => {
          setDialogOpen(false);
        },
      }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: getVehicleDefaultValues(),
    validationSchema: fleetVehicleValidationSchema,
    onSubmit,
  });

  const { errors, touched, handleSubmit, getFieldProps } = formik;

  const onDeleteVehicle = () => {
    mutateVehicle(
      {
        url: getTeamUrl(
          teamId,
          fleetVehicleIdUrl(operatorId, getVehicleId(deletingVehicle))
        ),
        method: "DELETE",
      },
      {
        onSuccess: () => {
          setDeletingVehicle(null);
        },
      }
    );
  };

  const onAddAssignment = () => {
    mutateAssignment(
      {
        url: getTeamUrl(teamId, fleetAssignmentsUrl(operatorId)),
        method: "POST",
        body: {
          driver_id: assignmentForm.driver_id,
          vehicle_id: assignmentForm.vehicle_id,
          start_at: toDateTimeString(assignmentForm.start_at),
        },
      },
      {
        onSuccess: () => {
          setAssignmentDialogOpen(false);
          setAssignmentForm({ driver_id: "", vehicle_id: "", start_at: "" });
        },
      }
    );
  };

  const onEndAssignment = () => {
    mutateAssignment(
      {
        url: getTeamUrl(
          teamId,
          fleetAssignmentEndUrl(operatorId, getAssignmentId(endingAssignment))
        ),
        method: "PUT",
      },
      {
        onSuccess: () => {
          setEndingAssignment(null);
        },
      }
    );
  };

  const driverNameById = useMemo(
    () =>
      drivers.reduce((acc, driver) => {
        acc[getDriverId(driver)] = fullName(driver);
        return acc;
      }, {}),
    [drivers]
  );

  const vehicleLabelById = useMemo(
    () =>
      vehicles.reduce((acc, vehicle) => {
        acc[getVehicleId(vehicle)] =
          vehicle?.registration_number ?? `#${getVehicleId(vehicle)}`;
        return acc;
      }, {}),
    [vehicles]
  );

  const vehicleColumns = useMemo(
    () => [
      {
        field: "registration_number",
        headerName: "Registration #",
        flex: 1,
      },
      {
        field: "make",
        headerName: "Make",
        flex: 0.8,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "model",
        headerName: "Model",
        flex: 0.8,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "year",
        headerName: "Year",
        flex: 0.5,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "vehicle_type",
        headerName: "Type",
        flex: 0.7,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "battery_capacity_kwh",
        headerName: "Battery (kWh)",
        flex: 0.7,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        renderCell: ({ value }) => (
          <Chip size="small" label={value ?? "-"} color={statusChipColor(value)} />
        ),
      },
      {
        field: "Actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row">
            {hasUpdateAccess && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(row)}>
                  <Icon icon="eva:edit-outline" />
                </IconButton>
              </Tooltip>
            )}
            {hasDeleteAccess && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => setDeletingVehicle(row)}>
                  <Icon icon="eva:trash-2-outline" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpdateAccess, hasDeleteAccess]
  );

  const assignmentColumns = useMemo(
    () => [
      {
        field: "driver_id",
        headerName: "Driver",
        flex: 1,
        renderCell: ({ value }) =>
          value ? driverNameById[value] ?? `#${value}` : "-",
      },
      {
        field: "vehicle_id",
        headerName: "Vehicle",
        flex: 1,
        renderCell: ({ value }) =>
          value ? vehicleLabelById[value] ?? `#${value}` : "-",
      },
      {
        field: "start_at",
        headerName: "Started",
        flex: 1,
        renderCell: ({ value }) => fDateTime(value) || "-",
      },
      {
        field: "end_at",
        headerName: "Ended",
        flex: 1,
        renderCell: ({ row, value }) =>
          value
            ? fDateTime(value)
            : Number(row?.active ?? 1) === 1
            ? "Active"
            : "-",
      },
      {
        field: "Actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <>
            {hasUpdateAccess && !row?.end_at && Number(row?.active ?? 1) === 1 && (
              <Tooltip title="End Assignment">
                <IconButton size="small" onClick={() => setEndingAssignment(row)}>
                  <Icon icon="eva:close-circle-outline" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      },
    ],
    [driverNameById, vehicleLabelById, hasUpdateAccess]
  );

  const dialogTextField = (name, label, extra = {}) => (
    <FormControl fullWidth size="small">
      <TextField
        size="small"
        label={label}
        {...getFieldProps(name)}
        error={Boolean(touched[name] && errors[name])}
        helperText={touched[name] && errors[name]}
        {...extra}
      />
    </FormControl>
  );

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Vehicles</Typography>
        {hasCreateAccess && (
          <Button
            variant="contained"
            startIcon={<Icon icon="eva:plus-fill" />}
            onClick={openCreate}
          >
            Add Vehicle
          </Button>
        )}
      </Stack>
      <BaseTable
        rows={vehicles}
        columns={vehicleColumns}
        loading={isFetching}
        getRowId={(row) => getVehicleId(row)}
        pageSize={10}
      />
      {!isFetching && !vehicles.length && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No vehicles added yet.
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Driver Assignments</Typography>
        {hasCreateAccess && (
          <Button
            variant="outlined"
            startIcon={<Icon icon="eva:plus-fill" />}
            onClick={() => setAssignmentDialogOpen(true)}
          >
            Assign Driver
          </Button>
        )}
      </Stack>
      <BaseTable
        rows={assignments}
        columns={assignmentColumns}
        loading={isAssignmentsFetching}
        getRowId={(row) => getAssignmentId(row)}
        pageSize={10}
      />
      {!isAssignmentsFetching && !assignments.length && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No driver-vehicle assignments yet.
        </Typography>
      )}

      {/* Add / edit vehicle dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingVehicle ? "Edit Vehicle" : "Add Vehicle"}
        </DialogTitle>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("registration_number", "Registration Number", {
                    required: true,
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("vin", "VIN")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("make", "Make")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("model", "Model")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("year", "Year", { type: "number" })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("vehicle_type", "Vehicle Type")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("battery_capacity_kwh", "Battery Capacity (kWh)", {
                    type: "number",
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="fleet-vehicle-status">Status</InputLabel>
                    <Select
                      labelId="fleet-vehicle-status"
                      label="Status"
                      {...getFieldProps("status")}
                    >
                      {FLEET_ENTITY_STATUS_OPTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="outlined" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isVehicleMutating}
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </Dialog>

      {/* Assign driver dialog */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign Driver to Vehicle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth size="small" required>
              <InputLabel id="assignment-driver">Driver</InputLabel>
              <Select
                labelId="assignment-driver"
                label="Driver"
                value={assignmentForm.driver_id}
                onChange={(e) =>
                  setAssignmentForm((form) => ({
                    ...form,
                    driver_id: e.target.value,
                  }))
                }
              >
                {drivers.map((driver) => (
                  <MenuItem key={getDriverId(driver)} value={getDriverId(driver)}>
                    {fullName(driver)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" required>
              <InputLabel id="assignment-vehicle">Vehicle</InputLabel>
              <Select
                labelId="assignment-vehicle"
                label="Vehicle"
                value={assignmentForm.vehicle_id}
                onChange={(e) =>
                  setAssignmentForm((form) => ({
                    ...form,
                    vehicle_id: e.target.value,
                  }))
                }
              >
                {vehicles.map((vehicle) => (
                  <MenuItem
                    key={getVehicleId(vehicle)}
                    value={getVehicleId(vehicle)}
                  >
                    {vehicle?.registration_number ?? `#${getVehicleId(vehicle)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Start At (optional)"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={assignmentForm.start_at}
              onChange={(e) =>
                setAssignmentForm((form) => ({
                  ...form,
                  start_at: e.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setAssignmentDialogOpen(false)}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disabled={!assignmentForm.driver_id || !assignmentForm.vehicle_id}
            loading={isAssignmentMutating}
            onClick={onAddAssignment}
          >
            Assign
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete vehicle confirm */}
      <MuiConfirmationDialog
        open={Boolean(deletingVehicle)}
        onClose={() => setDeletingVehicle(null)}
        title="Delete Vehicle"
        severity="error"
        contentText={`Are you sure you want to delete vehicle "${
          deletingVehicle?.registration_number ?? ""
        }"?`}
        closeText="Cancel"
        okText="Delete"
        onOk={onDeleteVehicle}
        isLoading={isVehicleMutating}
      />

      {/* End assignment confirm */}
      <MuiConfirmationDialog
        open={Boolean(endingAssignment)}
        onClose={() => setEndingAssignment(null)}
        title="End Assignment"
        severity="warning"
        contentText="End this driver-vehicle assignment now?"
        closeText="Cancel"
        okText="End Assignment"
        onOk={onEndAssignment}
        isLoading={isAssignmentMutating}
      />
    </>
  );
};

export default VehiclesTab;
